import { React, useRef, useState, useEffect, useCallback } from 'react';
import {
  useHistory,
  useParams,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatCard from 'components/Card/ChatCard';
import ImageLightbox from '../../components/ImageLightbox';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import FileUpload from '../../components/FileUpload/FileUpload';
import AddpayoutForm from '../../components/Forms/AddpayoutForm';
import EditExpenses from './EditExpenses';
import HrReportV2 from '../Humen/HrReportV2';
import { laborProjectReceiptPDF } from '../../components/PDF';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { isAnyExpenseApprover } from '../../utils/functions/approverAccess';
import { useSocket } from '../../contexts/SocketContext';
import { DevWinBadge } from '../../components/DevPageBadge';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',

  bgcolor: 'background.paper',

  boxShadow: 24,
  p: 4,
};

// approvalActions — ปุ่มจากผู้เรียก (modal ใน ExpensesTableApplove.js ส่งปุ่มชุดเดียวกับคอลัมน์ Actions มา)
// แสดงในกรอบตรงตำแหน่งปุ่มอนุมัติ/ปฏิเสธเดิมของหัวรายละเอียด แทนปุ่มสองตัวนั้น — เพราะปุ่มเดิมในนี้อนุมัติ
// PENDING → APPROVE ทันที (ไม่ไล่ APPROVE_1/APPROVE_2 เหมือนตาราง) ในโหมด THREE_STEP ที่อื่นที่ฝังไว้ไม่ส่ง
// prop นี้ คงปุ่มเดิม
export default function ExpensesDetail({
  title, subtitle, propId, onClose, approvalActions = null,
}) {
  const hideApprovalButtons = approvalActions != null;
  const params = useParams();
  const id = propId || params.id;
  const { search } = useLocation();
  const isEmbed = propId != null || new URLSearchParams(search).get('embed') === '1';
  const dispatch = useDispatch();
  const history = useHistory();
  const expenses = useSelector((state) => state.expenses);
  const me = useSelector((state) => state.me);
  const payout = useSelector((state) => state.payout);
  const chatExpenses = useSelector((state) => state.chatExpenses);
  const budget = useSelector((state) => state.budget);
  const project = useSelector((state) => state.project);
  const info = useSelector((state) => state.info);

  // สิทธิ์ปุ่มอนุมัติ/ปฏิเสธ — กติกาเดียวกับคอลัมน์ Actions ใน ExpensesTableApplove.js (renderApprovalButtons):
  // สถานะในขั้นอนุมัติ (PENDING/PREAPPROVE/APPROVE_1/APPROVE_2) เห็นปุ่มเฉพาะผู้อนุมัติขั้นนั้นตาม
  // info.expense_approver_1/2/3 (โหมด ONE_STEP = ผู้อนุมัติคนใดก็ได้ เฉพาะ PENDING/PREAPPROVE)
  // สถานะอื่นนอกขั้นอนุมัติไม่มีเรื่องสิทธิ์ผู้อนุมัติ คงพฤติกรรมเดิม
  const currentUserId = me?.userData?._id;
  const getIdValue = (approver) => approver?._id || approver || null;
  const canApprove1 = !!currentUserId && getIdValue(info?.expense_approver_1) === currentUserId;
  const canApprove2 = !!currentUserId && getIdValue(info?.expense_approver_2) === currentUserId;
  const canApprove3 = !!currentUserId && getIdValue(info?.expense_approver_3) === currentUserId;
  const isOneStep = (info?.expense_approval_mode || 'THREE_STEP') === 'ONE_STEP';
  const canActOnApproval = (status) => {
    if (!['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'].includes(status)) return true;
    if (isOneStep) {
      // ONE_STEP = ผู้อนุมัติช่องไหนก็ได้ใน 10 ช่อง (expense_approver_1..10)
      return ['PENDING', 'PREAPPROVE'].includes(status) && isAnyExpenseApprover(info, currentUserId);
    }
    if (status === 'APPROVE_1') return canApprove2;
    if (status === 'APPROVE_2') return canApprove3;
    return canApprove1;
  };

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [paidType, setPaidType] = useState('TRANSFER');
  const [cachedBudgetId, setCachedBudgetId] = useState(null);
  const [cachedBudgetRows, setCachedBudgetRows] = useState([]);
  const [addedImage, setAddedImage] = useState([]);
  const [isSavingImages, setIsSavingImages] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  // แกลเลอรีของ viewer — array ของ url รูป (ไม่รวม PDF) ที่เลื่อนก่อนหน้า/ถัดไปได้ + ตำแหน่งปัจจุบัน
  // เปิดจากที่ที่ส่งรูปเดียว (เช่น สลิป) จะเป็น [url] → มีรูปเดียว ไม่มีปุ่ม/ปัดเลื่อน
  const [viewerGallery, setViewerGallery] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [upSlip, setUpSlip] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [slipImage, setSlipImage] = useState([]);
  const [uploadSlipPayoutId, setUploadSlipPayoutId] = useState(null);
  const [uploadSlipImage, setUploadSlipImage] = useState([]);
  const [openSlipModal, setOpenSlipModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();
  const fetchChatExpenses = async () => {
    await dispatch(actions.chatExpensesAll({ ex: id }));
  };

  useEffect(() => {
    dispatch(actions.expensesGet(id));
    dispatch(actions.payoutAll({ ex: id }));
    fetchChatExpenses();
    // ใช้เช็คสิทธิ์ปุ่มอนุมัติ/ปฏิเสธ (canActOnApproval) — เปิดหน้านี้ตรงๆ จาก URL อาจยังไม่มีใน store
    if (_.isEmpty(info)) dispatch(actions.getInformation());
    if (_.isEmpty(me?.userData)) dispatch(actions.meGet());
    return () => {};
  }, []);

  // Re-fetch when parent page's expensesAll overwrites shared Redux state (race condition fix)
  useEffect(() => {
    if (id && expenses?.rows !== undefined && expenses?._id == null) {
      dispatch(actions.expensesGet(id));
    }
  }, [expenses?.rows]);

  // Socket.IO real-time listeners
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    const refresh = () => {
      dispatch(actions.expensesGet(id));
      dispatch(actions.payoutAll({ ex: id }));
    };
    const refreshChat = () => fetchChatExpenses();

    socket.on('expenses:updated', refresh);
    socket.on('payout:created', refresh);
    socket.on('payout:updated', refresh);
    socket.on('payout:deleted', refresh);
    socket.on('chat:created', refreshChat);

    return () => {
      socket.off('expenses:updated', refresh);
      socket.off('payout:created', refresh);
      socket.off('payout:updated', refresh);
      socket.off('payout:deleted', refresh);
      socket.off('chat:created', refreshChat);
    };
  }, [socket, id]);

  // Fetch budget data with computed fields (remain, percentage, totalStatus)
  // Cache budget ID and rows so they survive when state.expenses reverts to list format
  useEffect(() => {
    if (expenses?._id && expenses?.budget?._id) {
      setCachedBudgetId(String(expenses.budget._id));
    }
  }, [expenses?.budget?._id]);

  useEffect(() => {
    if (!_.isEmpty(budget?.rows)) {
      setCachedBudgetRows(budget.rows);
    }
  }, [budget?.rows]);

  useEffect(() => {
    // Guard: only run when we have a single expense, not the list format
    if (!expenses?._id) return;
    const projectId = expenses?.budget?.project_id || expenses?.project?._id;
    if (projectId) {
      dispatch(actions.budgetAll({ project_id: projectId }));
    }
    if (expenses?.project?._id) {
      dispatch(actions.projectGet(expenses?.project?._id));
    }
  }, [expenses?._id, expenses?.budget?.project_id, expenses?.project?._id]);

  const onSubmit = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (confirm) {
      const dataSubmit = {
        ...data,
        paidType,
        date,
        employee: me?.userData?._id,
        expenses: id,
        budget: expenses?.budget?._id,
        slipImage,
      };
      await dispatch(actions.payoutCreate(dataSubmit));
      await dispatch(actions.payoutAll({ ex: id }));
      dispatch(actions.expensesPut(id, { status: 'SUCCESS' }));
      dispatch(actions.expensesAll({}));
      reset();
      setSlipImage([]);
      handleClose();
    }
  };

  const handleOpen = () => {
    const net =
      expenses?.net_price != null
        ? Number(expenses.net_price)
        : (Number(expenses?.price) || 0) -
          (Number(expenses?.withholding_tax) || 0);
    setValue('price', net > 0 ? net : '');
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // เปิด viewer — image = รูปที่คลิก, gallery = ชุดรูปทั้งหมดที่ปัดเลื่อนได้ (ถ้าไม่ส่ง = โชว์รูปเดียว)
  const handleOpenModal = (image, gallery = []) => {
    const list = Array.isArray(gallery) ? gallery.filter(Boolean) : [];
    const finalList = list.length ? list : (image ? [image] : []);
    const idx = finalList.indexOf(image);
    setViewerGallery(finalList);
    setViewerIndex(idx >= 0 ? idx : 0);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setViewerGallery([]);
    setViewerIndex(0);
  };

  const handleDelete = async (id) => {
    const confirm = await confirmAsync('ยกเลิกรายการ');
    if (confirm) {
      try {
        await dispatch(actions.expensesPut(id, {
          status: 'CANCEL',
          approval_actor: me?.userData?._id,
          approval_actor_name: formatEmployeeThaiName(me?.userData),
        }));
        dispatch(actions.expensesGet(id));
      } catch (error) {}
    }
  };
  const handleToggleUrgent = async () => {
    try {
      await dispatch(actions.expensesToggleUrgent(id));
      await dispatch(actions.expensesGet(id));
    } catch (error) {
      showAlert(`ทำรายการไม่สำเร็จ: ${error?.message || error}`, { status: error?.response?.status });
    }
  };

  const handleReject = async (id) => {
    const confirmed = await confirmAsync('ปฏิเสธรายการเบิกนี้?');
    if (!confirmed) return;
    try {
      await dispatch(
        actions.expensesPut(id, {
          status: 'REJECT',
          approval_actor: me?.userData?._id,
          approval_actor_name: formatEmployeeThaiName(me?.userData),
        }),
      );
      await dispatch(actions.expensesGet(id));
    } catch (error) {
      console.error('Reject failed:', error);
      showAlert(`ปฏิเสธไม่สำเร็จ: ${error?.message || error}`, { status: error?.response?.status });
    }
  };

  // กู้คืนจากถังขยะ = CANCEL → REJECT (เหมือนปุ่ม "กู้คืน" ในตาราง ExpensesTabRejected.js handleRestoreFromTrash)
  // ไม่ใช่ → PENDING ตรงๆ — ให้กลับไปอยู่แท็บ "ปฏิเสธ" ก่อน แล้วค่อยแก้ไข/RESET เข้าคิวอนุมัติเอง
  const handleRestore = async (id) => {
    const confirmed = await confirmAsync('กู้คืนรายการเบิกนี้?');
    if (!confirmed) return;
    try {
      await dispatch(
        actions.expensesPut(id, {
          status: 'REJECT',
          approval_actor: me?.userData?._id,
          approval_actor_name: formatEmployeeThaiName(me?.userData),
        }),
      );
      await dispatch(actions.expensesGet(id));
    } catch (error) {
      console.error('Restore failed:', error);
      showAlert(`กู้คืนไม่สำเร็จ: ${error?.message || error}`, { status: error?.response?.status });
    }
  };

  const handlePermanentDelete = async (id) => {
    const confirm = await confirmAsync(
      'คุณต้องการลบใบเบิกนี้อย่างถาวรใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้',
    );
    if (confirm) {
      try {
        await dispatch(actions.expensesDelete(id));
        dispatch(actions.expensesAll({}));
        if (isEmbed) {
          onClose?.();
        } else {
          history.goBack();
        }
      } catch (error) {
        console.error('Failed to permanently delete expense:', error);
        showAlert('เกิดข้อผิดพลาดในการลบข้อมูล', { status: error?.response?.status });
      }
    }
  };

  const handleDeletePay = async (payoutId) => {
    const confirm = await confirmAsync('ลบข้อมูล');
    if (confirm) {
      await dispatch(actions.payoutDelete(payoutId));
      await dispatch(actions.payoutAll({ ex: id }));
    }
  };

  const handleEdit = () => {
    setOpenEditModal(true);
  };

  const handleApprove = async () => {
    const confirm = await confirmAsync('อนุมัติรายการเบิกนี้?');
    if (confirm) {
      try {
        await dispatch(
          actions.expensesPut(id, {
            status: 'APPROVE',
            approval_actor: me?.userData?.id,
            approval_actor_name: formatEmployeeThaiName(me?.userData),
          }),
        );
        dispatch(actions.expensesGet(id));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      PENDING: 'warning',
      APPROVE: 'info',
      SUCCESS: 'success',
      CANCEL: 'error',
      REJECT: 'error',
      HOLD: 'default',
      PREAPPROVE: 'info',
      PREPARE: 'warning',
    };
    return statusColors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      PENDING: 'รอการอนุมัติ',
      APPROVE: 'อนุมัติแล้ว',
      SUCCESS: 'จ่ายแล้ว',
      CANCEL: 'ยกเลิก',
      REJECT: 'ปฏิเสธ',
      HOLD: 'ระงับ',
      PREAPPROVE: 'ก่อนอนุมัติ',
      PREPARE: 'เตรียม',
    };
    return statusLabels[status] || status;
  };

  const renderDetail = () => (
    <Card
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
          >
            Expense Detail: {expenses?.code}
          </Typography>
          <Chip
            label={getStatusLabel(expenses?.status)}
            color={getStatusColor(expenses?.status)}
            variant="outlined"
            size="small"
          />
          {expenses?.is_urgent && (
            <Chip
              icon={<BoltIcon />}
              label="ด่วน"
              color="error"
              size="small"
            />
          )}
          {(expenses?.fuel_liters != null || expenses?.fuel_station) && (
            <Chip
              icon={<AutoAwesomeIcon />}
              label="ใช้ AI วิเคราะห์"
              color="info"
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {expenses?.status !== 'CANCEL' && (
            <Button
              variant={expenses?.is_urgent ? 'contained' : 'outlined'}
              color={expenses?.is_urgent ? 'error' : 'warning'}
              size="small"
              startIcon={<BoltIcon />}
              onClick={handleToggleUrgent}
            >
              {expenses?.is_urgent ? 'ยกเลิกด่วน' : 'ตั้งด่วน'}
            </Button>
          )}
          {approvalActions && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
                minWidth: 160,
              }}
            >
              {approvalActions}
            </Box>
          )}
          {/* Approve / Success buttons based on status */}
          {!hideApprovalButtons && expenses?.status === 'PENDING' && canActOnApproval(expenses?.status) && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={handleApprove}
            >
              อนุมัติ
            </Button>
          )}

          {expenses?.status !== 'APPROVE' &&
            expenses?.status !== 'SUCCESS' &&
            expenses?.status !== 'REJECT' &&
            expenses?.status !== 'CANCEL' &&
            !hideApprovalButtons &&
            canActOnApproval(expenses?.status) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleReject(id)}
              >
                ปฏิเสธ
              </Button>
            )}
          {expenses?.status !== 'CANCEL' && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => handleDelete(id)}
            >
              ยกเลิก
            </Button>
          )}
          {/* Edit */}
          {expenses?.status !== 'CANCEL' && expenses?.status === 'REJECT' && (
            <IconButton
              aria-label="edit"
              size="small"
              color="primary"
              onClick={() => handleEdit()}
            >
              <ModeEditIcon />
            </IconButton>
          )}
          {/* กู้คืน - แสดงเมื่อสถานะเป็น CANCEL */}
          {expenses?.status === 'CANCEL' && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<RestoreFromTrashIcon />}
              onClick={() => handleRestore(id)}
            >
              กู้คืน
            </Button>
          )}
          {/* ลบถาวร - แสดงเมื่อสถานะเป็น CANCEL */}
          {expenses?.status === 'CANCEL' && (
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handlePermanentDelete(id)}
            >
              ลบถาวร
            </Button>
          )}
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className="mb-2">
            <Typography variant="subtitle2" color="textSecondary">
              ประวัติการอนุมัติ
            </Typography>
            {Array.isArray(expenses?.approval_logs) &&
            expenses?.approval_logs?.length > 0 ? (
              <Box className="mt-1">
                {[...expenses.approval_logs]
                  .sort(
                    (a, b) =>
                      new Date(a?.acted_at || 0).getTime() -
                      new Date(b?.acted_at || 0).getTime(),
                  )
                  .map((log, idx) => (
                    <Box
                      key={`${log?._id || idx}`}
                      className="flex flex-wrap items-center gap-2 py-1"
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          log?.step
                            ? `ขั้นที่ ${log?.step}`
                            : log?.action || 'ACTION'
                        }
                      />
                      <Typography variant="body2">
                        {log?.status || '-'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        โดย {log?.actor_name || '-'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {log?.acted_at
                          ? dayjs(log.acted_at).format('DD/MM/YYYY HH:mm')
                          : '-'}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                ยังไม่มีประวัติการอนุมัติ
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Date
          </Typography>
          <Typography variant="body1">
            {dayjs(expenses?.date).format('DD/MM/YYYY')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Project
          </Typography>
          <Typography variant="body1">
            {expenses?.project?.name || '-'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Invoice Number
          </Typography>
          <Typography variant="body1">
            {expenses?.invoice_number || '-'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Customer
          </Typography>
          <Typography variant="body1">
            {expenses?.customer?.name || expenses?.project?.customer || '-'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Budget
          </Typography>
          <Typography variant="body1">
            {expenses?.budget?.prefix}
            {expenses?.budget?.budget_number} {expenses?.budget?.name}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            Applicant
          </Typography>
          <Typography variant="body1">
            {expenses?.isLabor
              ? 'ระบบอัติโนมัติ'
              : formatEmployeeThaiName(expenses?.employee) || '-'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary">
            Order
          </Typography>
          <Typography variant="body1">{expenses?.name}</Typography>
        </Grid>
        {expenses?.includes_vat && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Total (รวม VAT)
            </Typography>
            <Typography variant="body1" className="font-bold text-blue-600">
              ฿{expenses?.price?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
            </Typography>
          </Grid>
        )}
        {expenses?.includes_vat && expenses?.vat_amount != null && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary">
              VAT 7% (รวมอยู่ในยอด Total)
            </Typography>
            <Typography variant="body1" className="font-bold text-blue-400">
              ฿{expenses?.vat_amount?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
            </Typography>
          </Grid>
        )}
        {expenses?.withholding_tax != null && Number(expenses.withholding_tax) > 0 && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary">
              หัก ณ ที่จ่าย
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="warning.main">
              - ฿{Number(expenses.withholding_tax).toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary">
            ยอดจ่ายจริง
          </Typography>
          <Typography variant="h6" className="font-bold text-green-600">
            ฿{(expenses?.net_price ?? ((expenses?.price || 0) - (expenses?.withholding_tax || 0))).toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
          </Typography>
        </Grid>
        {/* Payee Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary">
            Payee
          </Typography>
          <Typography variant="body1">
            <b>Name:</b> {expenses?.payee?.name || '-'}
          </Typography>
          <Typography variant="body1">
            <b>Bank:</b> {expenses?.payee?.bank || '-'}
          </Typography>
          <Typography variant="body1">
            <b>Account Number:</b> {expenses?.payee?.account_number || '-'}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );

  // Budget & Project Analysis
  // Use cached values so budget card survives when state.expenses reverts to list format (socket refresh)
  const effectiveBudgetRows = _.isEmpty(budget?.rows) ? cachedBudgetRows : budget?.rows;
  const effectiveBudgetId = expenses?.budget?._id ? String(expenses.budget._id) : cachedBudgetId;
  const budgetData = _.find(
    effectiveBudgetRows,
    (b) => String(b?._id) === effectiveBudgetId,
  );

  // // DEBUG: Remove after fixing
  // console.log('=== BUDGET ANALYSIS DEBUG ===');
  // console.log('expenses?.budget?._id:', expenses?.budget?._id);
  // console.log('expenses?.budget?.project_id:', expenses?.budget?.project_id);
  // console.log('expenses?.project?._id:', expenses?.project?._id);
  // console.log('budget?.rows:', budget?.rows);
  // console.log('budgetData (matched):', budgetData);
  // console.log('project state:', project);
  // console.log('=== END DEBUG ===');

  const getNet = (e) =>
    Number(e?.net_price ?? (e?.price || 0) - (e?.withholding_tax || 0)) || 0;

  // Project-level totals — calculated locally from budget rows using the same method as the budget card
  // (SUCCESS only, net_price after WHT deduction) to avoid backend over-counting PENDING/CANCEL
  const projectTotalBudget =
    _.sumBy(effectiveBudgetRows, (b) => Number(b?.cost) || 0) || project?.total_budget || 0;
  const allBudgetExpenses = _.flatMap(effectiveBudgetRows, (b) => b?.expenses || []);
  const projectTotalExpenses = _.sumBy(
    _.filter(allBudgetExpenses, (e) => e?.status === 'SUCCESS'),
    getNet,
  );
  const projectExpected = _.sumBy(
    _.filter(allBudgetExpenses, (e) => e?.status === 'APPROVE'),
    getNet,
  );
  const projectRemain = projectTotalBudget - projectTotalExpenses - projectExpected;
  const projectPct = projectTotalBudget > 0 ? (projectTotalExpenses / projectTotalBudget) * 100 : 0;

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'error';
    if (pct >= 70) return 'warning';
    return 'success';
  };

  const formatMoney = (val) =>
    (val || 0)?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,');

  const MiniStat = ({ label, value, color = 'text.primary' }) => (
    <Box sx={{ textAlign: 'center', px: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontSize: '0.7rem' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" color={color}>
        ฿{formatMoney(value)}
      </Typography>
    </Box>
  );

  // คำนวณงบแบบเดียวกับ BudgetTable: Grand Total = SUCCESS, Expected = APPROVE, Remaining = cost - SUCCESS - APPROVE
  const getBudgetTableValues = (data) => {
    if (!data) return { budget: 0, grandTotal: 0, percent: 0, expected: 0, remaining: 0 };
    const successList = _.filter(data?.expenses, (e) => e?.status === 'SUCCESS');
    const approveList = _.filter(data?.expenses, (e) => e?.status === 'APPROVE');
    const grandTotal = _.sumBy(successList, getNet);
    const expected = _.sumBy(approveList, getNet);
    const cost = Number(data?.cost) || 0;
    const remaining = cost - grandTotal - expected;
    const percent = cost ? (grandTotal / cost) * 100 : 0;
    return { budget: cost, grandTotal, percent, expected, remaining };
  };

  const renderBudgetAnalysis = () => {
    const budgetValues = getBudgetTableValues(budgetData);
    const budgetPct = budgetValues.percent;

    return (
      <Grid container spacing={2}>
        {/* Project Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📊 โครงการ: {expenses?.project?.name || '-'}
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box
                className="flex justify-between items-center"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="caption" color="textSecondary">
                  ใช้งบไป
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color={getProgressColor(projectPct) + '.main'}
                >
                  {projectPct?.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(projectPct, 100)}
                color={getProgressColor(projectPct)}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <MiniStat label="งบประมาณ" value={projectTotalBudget} color="primary" />
              </Grid>
              <Grid item xs={3}>
                <MiniStat label="จ่ายแล้ว" value={projectTotalExpenses} color="error" />
              </Grid>
              <Grid item xs={3}>
                <MiniStat label="รออนุมัติ" value={projectExpected} color="warning.main" />
              </Grid>
              <Grid item xs={3}>
                <MiniStat label="คงเหลือ" value={projectRemain} color={projectRemain < 0 ? 'error' : 'success.main'} />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Budget Summary — ตรงกับตาราง Budget (Budget / Grand Total / Percent / Expected / Remaining) */}
        {budgetData && (
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💰 งบ: {budgetData?.prefix}
                {budgetData?.budget_number} — {budgetData?.name}
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box
                  className="flex justify-between items-center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Percent
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color={getProgressColor(Math.min(budgetPct, 100)) + '.main'}
                  >
                    {budgetPct?.toFixed(2)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(budgetPct, 100)}
                  color={getProgressColor(Math.min(budgetPct, 100))}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <MiniStat label="Budget" value={budgetValues.budget} color="primary" />
                </Grid>
                <Grid item xs={4}>
                  <MiniStat label="Grand Total" value={budgetValues.grandTotal} color="error" />
                </Grid>
                <Grid item xs={4}>
                  <MiniStat label="Expected" value={budgetValues.expected} color="warning.main" />
                </Grid>
                <Grid item xs={12}>
                  <MiniStat
                    label="Remaining"
                    value={budgetValues.remaining}
                    color={budgetValues.remaining < 0 ? 'error' : 'success.main'}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };
  // อัปโหลดรูปบิลทีละไฟล์ (multipart) แล้วค่อย $push id เข้า bill_images ผ่าน
  // /expenses/:id/images แทนการยัด base64 ทุกรูปรวมกันเข้า JSON เดียว (images: addedImage)
  // เดิม — กัน 413 Payload Too Large ตอนแนบรูปหลายรูป/ไฟล์ใหญ่ (ดู expenses.service.js addImages)
  const handleSaveImage = async () => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (confirm) {
      if (!_.isEmpty(addedImage)) {
        setIsSavingImages(true);
        try {
          const uploadedImageIds = await Promise.all(
            addedImage.map(async (img) => {
              const formData = new FormData();
              formData.append('file', img.file, img.name);
              const { data } = await api.post(
                `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
              );
              return data.id;
            }),
          );
          await dispatch(actions.expensesAddImages(id, uploadedImageIds));
          setAddedImage([]);
          await dispatch(actions.expensesGet(id));
        } catch (error) {
          // axios interceptor (api.js) โชว์ global error dialog ให้แล้วอัตโนมัติทุก request ที่ fail
          // ไม่ต้อง showAlert ซ้ำที่นี่อีก (จะเด้ง dialog ซ้อนกัน 2 อัน)
        } finally {
          setIsSavingImages(false);
        }
      }
    }
  };

  const handleRemoveImage = async (imageId) => {
    const confirm = await confirmAsync('ลบรูปนี้?');
    if (confirm) {
      await dispatch(actions.expensesRemoveImage(id, imageId));
      await dispatch(actions.expensesGet(id));
    }
  };

  const renderModal = () => (
    <ImageLightbox
      open={openModal}
      images={viewerGallery}
      index={viewerIndex}
      onIndexChange={setViewerIndex}
      onClose={handleCloseModal}
    />
  );

  const isPdfUrl = (url) => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.pdf');
  };

  // ปุ่ม "ดู PDF หลักฐานค่าแรง" — โผล่สำหรับรายการค่าแรง auto-sync (isLabor) ที่ยังไม่มี PDF จริงแนบใน
  // bill_images เลย (ไม่ผูกกับ status แล้ว เพราะตอนนี้ sync เข้าเป็น PENDING ตรงๆ ตั้งแต่แรก ไม่มี LABOR_DRAFT
  // คั่นให้เช็คอีกต่อไป) — ก่อนกดบันทึกเงินเดือนจริง (ยังไม่มี PDF จริง) ปุ่มนี้ทำ preview สดในเบราว์เซอร์
  // ไม่ upload/ไม่เก็บ พอบันทึกเงินเดือนจริงแล้ว PDF จริงจะถูกแนบเข้า bill_images ให้เอง (ดู HrReportV2.js
  // onSubmit → attachLaborReceipts) ปุ่มนี้จะหายไปเพราะมี bill_images จริงให้ดูแทนแล้ว
  const [previewingLabor, setPreviewingLabor] = useState(false);
  const hasNoBillImages = !expenses?.bill_image && (!expenses?.bill_images || expenses.bill_images.length === 0);

  // ExpensesDetail modal ไม่มี projectDashboard อยู่ในมือเลย (คำนวณสดแค่ตอนเปิดหน้า HrReportV2 เท่านั้น) —
  // mount HrReportV2 แบบซ่อนนอกจอ (lockPeriod) ให้คำนวณให้ แล้วดึงมาแค่แถวของโครงการนี้ไปสร้าง PDF แทนที่จะ
  // เขียนสูตรคำนวณซ้ำที่นี่ (เคยเป็นต้นเหตุบั๊กตัวเลขไม่ตรงมาก่อนถ้าคำนวณคนละจุดคนละสูตร)
  const handleLaborDashboardReady = useCallback((rows) => {
    const pid = expenses?.project?._id || expenses?.project?.id;
    const projectRow = (rows || []).find((r) => String(r.project_id) === String(pid));
    if (projectRow) {
      laborProjectReceiptPDF({
        code: expenses?.code,
        projectRow,
        dateStart: expenses.labor_period_start,
        dateEnd: expenses.labor_period_end,
        empType: expenses.labor_emp_type,
        me,
        open: true,
      });
    }
    setPreviewingLabor(false);
  }, [expenses, me]);

  const renderHeadlessLaborPreview = () => {
    if (!previewingLabor || !expenses?.labor_period_start || !expenses?.labor_period_end) return null;
    const empType = expenses.labor_emp_type || 'DAILY';
    const initial = {
      month: dayjs(expenses.labor_period_start).month(),
      year: dayjs(expenses.labor_period_start).year(),
      typeSelect: empType,
      selectedOption:
        empType === 'FULLTIME'
          ? 'Month'
          : dayjs(expenses.labor_period_start).date() <= 15 ? '15' : '31',
    };
    return (
      <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1400, height: 900, overflow: 'hidden', pointerEvents: 'none' }}>
        <HrReportV2
          key={expenses._id}
          initialMonth={initial.month}
          initialYear={initial.year}
          initialTypeSelect={initial.typeSelect}
          initialSelectedOption={initial.selectedOption}
          lockPeriod
          onDashboardReady={handleLaborDashboardReady}
        />
      </div>
    );
  };

  // รวม url รูป (ไม่รวม PDF) ทั้งหมดในส่วน Bill Images เรียงตามที่แสดงบนจอ — ส่งเข้า viewer เป็นแกลเลอรี
  // ให้เลื่อนก่อนหน้า/ถัดไปได้
  const billImageUrls = (() => {
    const arr = expenses?.bill_images || [];
    if (arr.length > 0) {
      return arr.filter((img) => !isPdfUrl(img.url)).map((img) => img.url);
    }
    if (expenses?.bill_image) return [expenses.bill_image.url];
    return [];
  })();

  const renderBillImage = () => (
    <div className="p-2 ">
      {expenses?.isLabor && hasNoBillImages && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AccessTimeIcon />}
            disabled={previewingLabor}
            onClick={() => setPreviewingLabor(true)}
          >
            {previewingLabor ? 'กำลังสร้าง PDF...' : 'ดู PDF หลักฐานค่าแรง'}
          </Button>
        </Box>
      )}
      <div className="text-xl">
        <b className="text-theme-600 py-2">Bill Images / Files</b>
      </div>
      {/* Display existing bill_images array */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Backward compatibility: show old single bill_image if no bill_images */}
        {expenses?.bill_image &&
          (!expenses?.bill_images || expenses?.bill_images?.length === 0) && (
            <Grid item xs={6} sm={4} md={3}>
              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                }}
                onClick={() => handleOpenModal(expenses.bill_image.url, billImageUrls)}
              >
                <img
                  src={expenses.bill_image.url}
                  alt="Bill Image"
                  style={{ width: '100%', height: 150, objectFit: 'cover' }}
                />
              </Box>
            </Grid>
          )}
        {/* Display bill_images array — images and PDFs */}
        {expenses?.bill_images?.map((img, index) => (
          <Grid item xs={6} sm={4} md={3} key={img._id || index}>
            <Box
              sx={{
                position: 'relative',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {isPdfUrl(img.url) ? (
                <Box
                  component="a"
                  href={img.url}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 150,
                    cursor: 'pointer',
                    bgcolor: 'grey.50',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 48, color: 'error.main' }} />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    เปิดไฟล์ PDF
                  </Typography>
                  <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }} />
                </Box>
              ) : (
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOpenModal(img.url, billImageUrls)}
                >
                  <img
                    src={img.url}
                    alt={`Bill ${index + 1}`}
                    style={{ width: '100%', height: 150, objectFit: 'cover' }}
                  />
                </Box>
              )}
              <IconButton
                size="small"
                color="error"
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(255,255,255,0.8)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                }}
                onClick={() => handleRemoveImage(img._id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>
      {/* Upload new images / PDFs */}
      <div className="py-2">
        <FileUpload
          maxNumber={10}
          files={addedImage}
          setFiles={setAddedImage}
          title={'อัปโหลดรูปบิล / PDF'}
          accept="image/*,.pdf"
        />
        <div>
          {!_.isEmpty(addedImage) && (
            <div className="flex justify-center mt-2">
              <Button variant="contained" disabled={isSavingImages} onClick={() => handleSaveImage()}>
                {isSavingImages ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleUploadSlip = (payoutId) => {
    setUploadSlipPayoutId(payoutId);
    setUploadSlipImage([]);
    setOpenSlipModal(true);
  };

  const handleSaveSlip = async () => {
    if (!uploadSlipPayoutId || _.isEmpty(uploadSlipImage)) return;
    const confirmSave = await confirmAsync('บันทึกสลิป');
    if (confirmSave) {
      await dispatch(
        actions.payoutPut(uploadSlipPayoutId, {
          slipImage: uploadSlipImage,
        }),
      );
      setOpenSlipModal(false);
      setUploadSlipImage([]);
      setUploadSlipPayoutId(null);
      await dispatch(actions.payoutAll({ ex: id }));
    }
  };

  const renderSlipUploadModal = () => (
    <Modal
      open={openSlipModal}
      onClose={() => setOpenSlipModal(false)}
      aria-labelledby="slip-upload-modal"
    >
      <Box sx={style}>
        <Typography variant="h6" className="mb-2">
          อัปโหลดสลิปการโอน
        </Typography>
        <ImageUpload
          preview_size="300"
          maxNumber={1}
          images={uploadSlipImage}
          setImages={setUploadSlipImage}
          title={'เลือกสลิป'}
        />
        {!_.isEmpty(uploadSlipImage) && (
          <div className="flex justify-center mt-3">
            <Button variant="contained" onClick={handleSaveSlip}>
              บันทึกสลิป
            </Button>
          </div>
        )}
      </Box>
    </Modal>
  );

  const renderPayment = () => (
    <div>
      <div className="p-2 flex ">
        <div className="text-xl">
          {' '}
          <b className="text-theme-600">Payment</b>
        </div>
        <div className="px-4">
          <Button size="small" variant="contained" onClick={() => handleOpen()}>
            Add payment
          </Button>
        </div>
      </div>

      {!_.isEmpty(payout?.rows) ? (
        _.map(payout?.rows, (e) => (
          <div className="px-1 py-1" key={e._id}>
            <Card>
              <div className="flex">
                <div className="p-2 flex-1">
                  <div>Payment Date {dayjs(e?.date).format('DD/MM/YYYY')}</div>
                  <div>
                    Paid{' '}
                    {e?.price?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </div>
                  <div>Method : {e?.paidType}</div>
                  <div>Remark : {e?.remark || '-'}</div>
                  {/* Slip Image */}
                  {e?.slip?.url ? (
                    <div className="mt-2">
                      <Typography variant="caption" color="textSecondary">
                        สลิปการโอน
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          display: 'inline-block',
                          '&:hover': { boxShadow: 3 },
                        }}
                        onClick={() => handleOpenModal(e.slip.url)}
                      >
                        <img
                          src={e.slip.url}
                          alt="Slip"
                          style={{
                            width: 150,
                            height: 150,
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReceiptLongIcon />}
                        onClick={() => handleUploadSlip(e._id)}
                      >
                        อัปโหลดสลิป
                      </Button>
                    </div>
                  )}
                </div>
                <div className="py-1">
                  <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={() => handleDeletePay(e._id)}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </div>
            </Card>
          </div>
        ))
      ) : (
        <div className="px-1 py-1">
          <Card>
            <div className="p-2">Waiting for payment</div>
          </Card>
        </div>
      )}
    </div>
  );

  const renderModalAddPayment = () => (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
          ></Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <AddpayoutForm
              control={control}
              Controller={Controller}
              date={date}
              setDate={setDate}
              paidType={paidType}
              slipImage={slipImage}
              setSlipImage={setSlipImage}
              expenseData={expenses}
            />
          </form>
        </Box>
      </Modal>
    </div>
  );

  const onSubmitChat = () => {
    const dataSubmit = {
      sender: me?.userData?._id,
      message: chatMessage,
      expenses_id: id,
    };
    dispatch(actions.chatExpensesCreate(dataSubmit)).then(() => {
      setChatMessage('');
      fetchChatExpenses();
    });
  };

  const renderChatSection = () => (
    <ChatCard
      me={me}
      chatExpenses={chatExpenses}
      chatMessage={chatMessage}
      setChatMessage={setChatMessage}
      onSubmitChat={onSubmitChat}
    />
  );

  return (
    <div>
      <DevWinBadge num={132} name="ExpenseDetail · Add Payment" open={open} />
      {renderModalAddPayment()}
      <DevWinBadge num={133} name="ExpenseDetail · Edit Expense" open={openModal} />
      {renderModal()}
      <DevWinBadge num={134} name="ExpenseDetail · Slip Upload" open={openSlipModal} />
      {renderSlipUploadModal()}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="subtitle1" fontWeight="bold">แก้ไขรายการเบิกจ่าย</Typography>
          <IconButton size="small" onClick={() => setOpenEditModal(false)} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          {openEditModal && (
            <EditExpenses
              propId={id}
              onClose={() => {
                setOpenEditModal(false);
                dispatch(actions.expensesGet(id));
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <div>{renderDetail()}</div>
      <div className="py-2">{renderBudgetAnalysis()}</div>
      <div className="py-2">{renderBillImage()}</div>
      <div className="py-2">{renderPayment()}</div>
      <div className="py-2">{renderChatSection()}</div>
      {renderHeadlessLaborPreview()}
    </div>
  );
}
